import mixpanel from 'mixpanel-browser'
import { posthogClient } from '../posthog/posthog.service'
import { dataLayerService } from './dataLayerService'
import type { AnalyticsConfig, EventProperties, UserProperties } from './types'

class MixpanelService {
  private initialized = false

  /**
   * Initialize Mixpanel with the provided configuration
   */
  init(config: AnalyticsConfig): void {
    if (this.initialized) {
      console.warn('Mixpanel already initialized')
      return
    }

    mixpanel.init(config.token, {
      debug: config.debug || false,
      track_pageview: config.trackAutomaticEvents || false,
      persistence: config.persistence || 'cookie',
      ignore_dnt: config.ignoreDnt ?? false,
      secure_cookie: config.secureCookie ?? window.location.protocol === 'https:',
      cookie_domain: config.cookieDomain,
      cross_subdomain_cookie: config.crossSubdomainCookie ?? true,
    })

    this.initialized = true
    console.log('Mixpanel initialized successfully')
  }

  /**
   * Check if Mixpanel has been initialized
   */
  isInitialized(): boolean {
    return this.initialized
  }

  /**
   * Track an event with optional properties
   */
  track(eventName: string, properties?: EventProperties): void {
    posthogClient.capture(eventName, properties)

    if (!this.initialized) {
      console.warn('Mixpanel not initialized. Call init() first.')
      return
    }

    try {
      mixpanel.track(eventName, properties)
      dataLayerService.track(eventName, properties)
    } catch (error) {
      console.error('Failed to track event:', error)
    }
  }

  /**
   * Identify a user
   */
  identify(userId: string): void {
    posthogClient.identify(userId)

    if (!this.initialized) {
      console.warn('Mixpanel not initialized. Call init() first.')
      return
    }

    try {
      mixpanel.identify(userId)
    } catch (error) {
      console.error('Failed to identify user:', error)
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: UserProperties): void {
    posthogClient.setPersonProperties(properties)

    if (!this.initialized) {
      console.warn('Mixpanel not initialized. Call init() first.')
      return
    }

    try {
      mixpanel.people.set(properties)
    } catch (error) {
      console.error('Failed to set user properties:', error)
    }
  }

  /**
   * Register super-properties that are sent with every subsequent event
   */
  registerSuperProperties(
    properties: Record<string, string | number | boolean | null | undefined>
  ): void {
    posthogClient.register(properties)

    if (!this.initialized) {
      console.warn('Mixpanel not initialized. Call init() first.')
      return
    }

    try {
      mixpanel.register(properties)
    } catch (error) {
      console.error('Failed to register super properties:', error)
    }
  }

  /**
   * Register super-properties only if not already set (first-touch semantics)
   */
  registerSuperPropertiesOnce(
    properties: Record<string, string | number | boolean | null | undefined>
  ): void {
    posthogClient.registerOnce(properties)

    if (!this.initialized) {
      console.warn('Mixpanel not initialized. Call init() first.')
      return
    }

    try {
      mixpanel.register_once(properties)
    } catch (error) {
      console.error('Failed to register super properties once:', error)
    }
  }

  /**
   * Associate subsequent events with a group (PostHog group analytics).
   * Mixpanel needs no equivalent: business_id rides along as a super property.
   */
  group(groupType: string, groupKey: string): void {
    posthogClient.group(groupType, groupKey)
  }

  /**
   * Reset user session (useful for logout)
   */
  reset(): void {
    posthogClient.reset()

    if (!this.initialized) {
      console.warn('Mixpanel not initialized. Call init() first.')
      return
    }

    try {
      mixpanel.reset()
    } catch (error) {
      console.error('Failed to reset user session:', error)
    }
  }

  /**
   * Get the current distinct ID
   */
  getDistinctId(): string | undefined {
    if (!this.initialized) {
      console.warn('Mixpanel not initialized. Call init() first.')
      return undefined
    }

    try {
      return mixpanel.get_distinct_id()
    } catch (error) {
      console.error('Failed to get distinct ID:', error)
      return undefined
    }
  }
}

// Export singleton instance
export const analytics = new MixpanelService()
